/**
 * Expense API to Entity Mappers
 * Maps API responses to internal entity models
 */

import { API } from '../api_clients';
import {
  Expense,
  ExpenseDriver,
  ExpenseLoad,
  ExpenseListItem,
  ExpenseListSummary,
  ExpenseTruck,
} from '../../types/expense';
import { ExpenseCategory, PaymentMethod } from '../../types/enums';

// Category mapping (case-insensitive)
const expenseCategoryMap: Record<string, ExpenseCategory> = {
  fuel: ExpenseCategory.FUEL,
  maintenance: ExpenseCategory.MAINTENANCE,
  repair: ExpenseCategory.REPAIR,
  insurance: ExpenseCategory.INSURANCE,
  tolls: ExpenseCategory.TOLLS,
  permits: ExpenseCategory.PERMITS,
  parking: ExpenseCategory.PARKING,
  scale: ExpenseCategory.SCALE,
  lumper: ExpenseCategory.LUMPER,
  detention: ExpenseCategory.DETENTION,
  driver_advance: ExpenseCategory.DRIVER_ADVANCE,
  driver_pay: ExpenseCategory.DRIVER_PAY,
  office: ExpenseCategory.OFFICE,
  equipment: ExpenseCategory.EQUIPMENT,
  lease: ExpenseCategory.LEASE,
  taxes: ExpenseCategory.TAXES,
  other: ExpenseCategory.OTHER,
};

// Payment method mapping (case-insensitive)
const paymentMethodMap: Record<string, PaymentMethod> = {
  check: PaymentMethod.CHECK,
  ach: PaymentMethod.ACH,
  wire: PaymentMethod.WIRE,
  credit_card: PaymentMethod.CREDIT_CARD,
  cash: PaymentMethod.CASH,
  factoring: PaymentMethod.FACTORING,
};

function mapExpenseCategory(category?: string): ExpenseCategory {
  if (!category) return ExpenseCategory.OTHER;
  return expenseCategoryMap[category.toLowerCase()] || ExpenseCategory.OTHER;
}

function mapPaymentMethod(method?: string | null): PaymentMethod | null {
  if (!method) return null;
  return paymentMethodMap[method.toLowerCase()] || null;
}

function mapExpenseDriver(driver?: any): ExpenseDriver | null {
  if (!driver) return null;
  return {
    id: driver.unid || '',
    name: driver.name || '',
    phone: driver.phone || null,
    photoUrl: driver.photo_url || null,
    type: driver.type || null,
  };
}

function mapExpenseTruck(truck?: any): ExpenseTruck | null {
  if (!truck) return null;
  return {
    id: truck.unid || '',
    unitNumber: truck.unit_number || null,
    type: truck.type || null,
  };
}

function mapExpenseLoad(load?: any): ExpenseLoad | null {
  if (!load) return null;
  return {
    id: load.id || '',
    loadNumber: load.loadNumber || null,
  };
}

/**
 * Map API expense to full entity expense
 */
export function mapApiExpenseToEntity(apiExpense: API.ResponsesExpense): Expense {
  return {
    // ID
    id: apiExpense.unid || '',

    // Core expense details
    amount: apiExpense.amount || 0,
    category: mapExpenseCategory(apiExpense.category),
    date: apiExpense.date || new Date().toISOString(),
    description: apiExpense.description || null,
    vendor: apiExpense.vendor || null,

    // Payment info
    paymentMethod: mapPaymentMethod(apiExpense.payment_method),
    cardLastFour: apiExpense.card_last_four || null,

    // Related entities
    driverId: apiExpense.driver_id || null,
    truckId: apiExpense.truck_id || null,
    loadId: apiExpense.load_id || null,
    receiptDocId: apiExpense.receipt_doc_id || null,

    // Nested related entities
    driver: mapExpenseDriver((apiExpense as any).driver),
    truck: mapExpenseTruck((apiExpense as any).truck),
    load: mapExpenseLoad((apiExpense as any).load),

    // Metadata
    createdAt: apiExpense.createdAt || new Date().toISOString(),
    updatedAt: apiExpense.updatedAt || new Date().toISOString(),
  };
}

/**
 * Map API expense to list item entity (for table views)
 */
export function mapApiExpenseToListItem(apiExpense: API.ResponsesExpense): ExpenseListItem {
  return {
    id: apiExpense.unid || '',

    // Core expense details
    amount: apiExpense.amount || 0,
    category: mapExpenseCategory(apiExpense.category),
    date: apiExpense.date || new Date().toISOString(),
    description: apiExpense.description || null,
    vendor: apiExpense.vendor || null,

    // Payment info
    paymentMethod: mapPaymentMethod(apiExpense.payment_method),
    cardLastFour: apiExpense.card_last_four || null,

    // Related entities
    driverId: apiExpense.driver_id || null,
    truckId: apiExpense.truck_id || null,
    loadId: apiExpense.load_id || null,

    // Nested related entities
    driver: mapExpenseDriver((apiExpense as any).driver),
    truck: mapExpenseTruck((apiExpense as any).truck),
    load: mapExpenseLoad((apiExpense as any).load),

    // Metadata
    createdAt: apiExpense.createdAt || new Date().toISOString(),
  };
}

/**
 * Map API expense list summary to entity
 */
export function mapApiExpenseListSummaryToEntity(
  apiSummary?: API.ResponsesExpenseListSummary
): ExpenseListSummary {
  return {
    totalAmount: apiSummary?.total_amount || 0,
    byCategory: apiSummary?.by_category || {},
  };
}
